import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaPenToSquare, FaEnvelope, FaTrash } from 'react-icons/fa6';
import { useAppContext } from '../../../../shared/context/AppContext';
import { deletePromptDoc } from '../../../../shared/data/promptsRepo';
import { useAnalytics } from '../../../../shared/hooks/useAnalytics';
import { EVENTS } from '../../../../shared/services/analyticsEvents';
import { getUserId } from '../../../../shared/domain/services/userService';
import * as editPromptController from '../controllers/editPromptController';
import * as categoryService from '../../../promptLibrary/data/services/categoryService';
import * as promptBuilderController from '../../../../shared/promptForm/promptBuilderController';
import { parsePromptText } from '../utils/parsePromptText';
import { detectPromptFormat } from '../../domain/services/promptFormatDetector';
import { getAllOrgDomains, getOrgDomain } from '../../../../shared/utils/organizationDomainUtil';
import PromptActionButtons from './PromptActionButtons';
import PersonaInput from '../../../../shared/promptForm/components/PersonaInput';
import FormatInput from '../../../../shared/promptForm/components/FormatInput';
import ContextInput from '../../../../shared/promptForm/components/ContextInput';
import TaskInput from '../../../../shared/promptForm/components/TaskInput';
import DosInput from '../../../../shared/promptForm/components/DosInput';
import DontsInput from '../../../../shared/promptForm/components/DontsInput';
import ExamplesInput from '../../../../shared/promptForm/components/ExamplesInput';
import CategoryDropdown from '../../../addPrompt/presentation/components/CategoryDropdown';
import BackgroundTheoryInput from '../../../addPrompt/presentation/components/BackgroundTheoryInput';
import PromptFormatTabs from '../../../addPrompt/presentation/components/PromptFormatTabs';
import Button from '../../../../shared/components/Button';
import '../../../../shared/promptForm/components/FormInput.css';
import '../../../contextVariables/presentation/screens/ContextVariablesScreen.css';
import '../../../contextVariables/presentation/components/ContextVariableForm.css';
import './PromptDetails.css';
import './PromptCard.css';

const PromptHeader = ({ prompt, duplicate, activeVersion, onUpdate, onEditModeChange, onDuplicate, shouldStartInEditMode = false }) => {
  const { user, organization } = useAppContext();
  const { trackEvent } = useAnalytics();
  const [isEditMode, setIsEditMode] = useState(shouldStartInEditMode);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState('');
  
  // Form state
  const [promptFormat, setPromptFormat] = useState('STRUCTURED');
  const [formData, setFormData] = useState({
    title: prompt.title || '',
    persona: '',
    format: '',
    context: '',
    task: '',
    dos: '',
    donts: '',
    examples: '',
    category: [],
    backgroundTheory: prompt.backgroundTheory || '',
    description: prompt.description || '',
    // Free text field
    freeText: '',
  });

  const userId = getUserId(user);
  const isAdmin = user?.role && user.role.toLowerCase() === 'admin';
  const isOwnPrompt = user && userId && prompt.creatorId === userId;
  // Check if viewing a duplicate that belongs to the user
  // Duplicates have userId and originalPromptId properties (not creatorId)
  const isViewingMyDuplicate = activeVersion === 'duplicate' && duplicate && duplicate.userId === userId;
  // Also check if the prompt object itself is a duplicate (has userId and originalPromptId)
  const isCurrentPromptDuplicate = prompt && prompt.userId === userId && prompt.originalPromptId;
  // Show edit button if:
  // 1. It's own prompt (original prompt created by user), OR
  // 2. Viewing "My Version" (duplicate) that belongs to the user, OR
  // 3. User is admin (admins can edit any prompt)
  const canEdit = isOwnPrompt || isViewingMyDuplicate || isCurrentPromptDuplicate || isAdmin;
  const isGlobalPrompt =
    prompt &&
    (prompt.isGlobal === true ||
      prompt.isGlobal === 1 ||
      prompt.isGlobal === '1' ||
      prompt.isGlobal === 'true');
  const canDeletePrompt = isOwnPrompt || (isAdmin && isGlobalPrompt);
  
  // Org assignment state (for admins editing prompts)
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [orgDomains, setOrgDomains] = useState([]);
  const [currentOrgDomain, setCurrentOrgDomain] = useState(null);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  // Load categories
  const loadCategories = useCallback(async () => {
    const orgId = organization?.id || organization?.orgId;
    // Always fetch categories for user's org OR global categories
    // Pass orgId explicitly (even if null) to enable OR mode in backend
    const cats = await categoryService.getAllCategories({ orgId: orgId || null, isGlobal: true });
    setCategories(cats);
  }, [organization]);

  // Load organizations for admin org assignment
  const loadOrganizations = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      setLoadingOrgs(true);
      const orgs = await getAllOrgDomains();
      setOrgDomains(orgs);
      
      // Set current org assignment (for both global and org-specific prompts)
      const activePrompt = activeVersion === 'duplicate' && duplicate ? duplicate : prompt;
      const promptOrgId = activePrompt.organizationId || activePrompt.orgId;
      const isPromptGlobal = activePrompt.isGlobal === true || activePrompt.isGlobal === 1 || activePrompt.isGlobal === '1' || activePrompt.isGlobal === 'true';
      
      if (isPromptGlobal || !promptOrgId) {
        // Global prompt
        setSelectedOrgId(null);
        setCurrentOrgDomain('Global (all organizations)');
      } else {
        // Org-specific prompt
        setSelectedOrgId(promptOrgId);
        const domain = await getOrgDomain(promptOrgId);
        setCurrentOrgDomain(domain || promptOrgId);
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoadingOrgs(false);
    }
  }, [isAdmin, prompt, duplicate, activeVersion]);

  useEffect(() => {
    loadCategories();
    if (isAdmin) {
      loadOrganizations();
    }
  }, [loadCategories, loadOrganizations, isAdmin]);

  useEffect(() => {
    if (onEditModeChange) {
      onEditModeChange(isEditMode);
    }
  }, [isEditMode, onEditModeChange]);

  // Handle external edit mode trigger (e.g., when duplicate is created)
  useEffect(() => {
    if (shouldStartInEditMode) {
      setIsEditMode(true);
    }
  }, [shouldStartInEditMode]);

  useEffect(() => {
    // Initialize form data from prompt (use duplicate if active version is duplicate, otherwise use original prompt)
    const activePrompt = activeVersion === 'duplicate' && duplicate ? duplicate : prompt;
    
    // Detect prompt format
    const detectedFormat = detectPromptFormat(activePrompt);
    setPromptFormat(detectedFormat);
    
    // Get categories array, fallback to single category for backward compatibility
    let promptCategories = [];
    if (activePrompt.categories && Array.isArray(activePrompt.categories) && activePrompt.categories.length > 0) {
      promptCategories = activePrompt.categories;
    } else if (activePrompt.category) {
      promptCategories = [activePrompt.category];
    }

    // Parse fields based on format
    let parsedFields = {};
    if (detectedFormat === 'FREE_TEXT') {
      parsedFields = { freeText: activePrompt.promptText || '' };
    } else {
      // STRUCTURED format
      parsedFields = parsePromptText(activePrompt.promptText || '');
    }

    setFormData({
      title: activePrompt.title || '',
      persona: parsedFields.persona || '',
      format: parsedFields.format || '',
      context: parsedFields.context || '',
      task: parsedFields.task || '',
      dos: parsedFields.dos || '',
      donts: parsedFields.donts || '',
      examples: parsedFields.examples || '',
      category: promptCategories,
      backgroundTheory: activePrompt.backgroundTheory || '',
      description: activePrompt.description || '',
      // Free text field
      freeText: parsedFields.freeText || '',
    });
  }, [prompt, duplicate, activeVersion]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddCategory = async (categoryName) => {
    try {
      const orgId = organization?.id || organization?.orgId;
      const filters = orgId ? { orgId, isGlobal: true } : { isGlobal: true };
      
      // Check if category already exists
      const exists = await categoryService.categoryExists(categoryName, filters);
      
      if (exists) {
        // Category already exists, add it to selection if not already selected
        const currentCategories = Array.isArray(formData.category) ? formData.category : (formData.category ? [formData.category] : []);
        if (!currentCategories.includes(categoryName)) {
          handleInputChange('category', [...currentCategories, categoryName]);
        }
      } else {
        // Category doesn't exist, add it
        await categoryService.addCategory(categoryName, { orgId });
        const updatedCategories = await categoryService.getAllCategories(filters);
        setCategories(updatedCategories);
        // Add the newly added category to selection
        const currentCategories = Array.isArray(formData.category) ? formData.category : (formData.category ? [formData.category] : []);
        handleInputChange('category', [...currentCategories, categoryName]);
      }
    } catch (error) {
      console.error('Error adding category:', error);
      const errorMessage = error.message || 'Failed to add category. Please try again.';
      alert(`Failed to add category: ${errorMessage}`);
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Reset form data to original prompt values
    const activePrompt = activeVersion === 'duplicate' && duplicate ? duplicate : prompt;
    const detectedFormat = detectPromptFormat(activePrompt);
    setPromptFormat(detectedFormat);
    
    let parsedFields = {};
    if (detectedFormat === 'FREE_TEXT') {
      parsedFields = { freeText: activePrompt.promptText || '' };
    } else {
      parsedFields = parsePromptText(activePrompt.promptText || '');
    }

    let promptCategories = [];
    if (activePrompt.categories && Array.isArray(activePrompt.categories) && activePrompt.categories.length > 0) {
      promptCategories = activePrompt.categories;
    } else if (activePrompt.category) {
      promptCategories = [activePrompt.category];
    }
    setFormData({
      title: activePrompt.title || '',
      persona: parsedFields.persona || '',
      format: parsedFields.format || '',
      context: parsedFields.context || '',
      task: parsedFields.task || '',
      dos: parsedFields.dos || '',
      donts: parsedFields.donts || '',
      examples: parsedFields.examples || '',
      category: promptCategories,
      backgroundTheory: activePrompt.backgroundTheory || '',
      description: activePrompt.description || '',
      freeText: parsedFields.freeText || '',
    });
  };

  const handleFormatChange = (format) => {
    setPromptFormat(format);
  };

  const handleSaveEdit = async () => {
    if (!user) return;

    // Validate mandatory fields based on format
    const missingFields = [];
    
    if (!formData.title || formData.title.trim() === '') {
      missingFields.push('Title');
    }
    
    if (!formData.category || (Array.isArray(formData.category) && formData.category.length === 0)) {
      missingFields.push('Category');
    }
    
    if (!formData.description || formData.description.trim() === '') {
      missingFields.push('Description');
    }
    
    // Format-specific validation
    if (promptFormat === 'STRUCTURED') {
      if (!formData.persona || formData.persona.trim() === '') {
        missingFields.push('Persona');
      }
      if (!formData.format || formData.format.trim() === '') {
        missingFields.push('Format');
      }
      if (!formData.context || formData.context.trim() === '') {
        missingFields.push('Context');
      }
      if (!formData.task || formData.task.trim() === '') {
        missingFields.push('Task');
      }
      if (!formData.dos || formData.dos.trim() === '') {
        missingFields.push('Do');
      }
      if (!formData.donts || formData.donts.trim() === '') {
        missingFields.push("Don't");
      }
    } else if (promptFormat === 'FREE_TEXT') {
      if (!formData.freeText || formData.freeText.trim() === '') {
        missingFields.push('Prompt Text');
      }
    }
    
    if (missingFields.length > 0) {
      alert(`Please fill in all mandatory fields: ${missingFields.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      // Rebuild promptText from form fields based on format
      let promptText = '';
      if (promptFormat === 'FREE_TEXT') {
        promptText = formData.freeText;
      } else {
        // STRUCTURED format
        promptText = promptBuilderController.buildPrompt(formData);
      }
      
      const updates = {
        title: formData.title,
        description: formData.description,
        promptText: promptText,
        backgroundTheory: formData.backgroundTheory || null,
        categories: formData.category, // Array of category names
        promptFormat: promptFormat,
      };
      
      // Admins can change orgId assignment (for both global and org-specific prompts)
      if (isAdmin && selectedOrgId !== undefined) {
        const activePrompt = activeVersion === 'duplicate' && duplicate ? duplicate : prompt;
        const isPromptGlobal = activePrompt.isGlobal === true || activePrompt.isGlobal === 1 || activePrompt.isGlobal === '1' || activePrompt.isGlobal === 'true';
        const currentOrgId = activePrompt.organizationId || activePrompt.orgId;
        
        // Determine if we need to update
        const newIsGlobal = selectedOrgId === null;
        const needsUpdate = newIsGlobal !== isPromptGlobal || (!newIsGlobal && selectedOrgId !== currentOrgId);
        
        if (needsUpdate) {
          if (selectedOrgId === null) {
            // Converting to global
            updates.isGlobal = true;
          } else {
            // Converting to org-specific or changing orgId
            updates.orgId = selectedOrgId;
            updates.organizationId = selectedOrgId;
          }
        }
      }
      
      // Check if we're editing a duplicate
      const isEditingDuplicate = activeVersion === 'duplicate' && duplicate;
      
      let updatedPrompt;
      if (isEditingDuplicate) {
        updatedPrompt = await editPromptController.updateDuplicate(duplicate.id, updates);
      } else {
        updatedPrompt = await editPromptController.updatePrompt(prompt.id, updates);
      }
      
      setIsEditMode(false);
      if (onUpdate) {
        onUpdate(updatedPrompt);
      }
      // Track prompt edit event
      trackEvent(EVENTS.PROMPT_EDITED, {
        promptId: isEditingDuplicate ? duplicate.id : prompt.id,
        promptTitle: formData.title,
        categories: Array.isArray(formData.category) ? formData.category : [formData.category],
        isGlobal: prompt.isGlobal || false,
        isDuplicate: isEditingDuplicate,
      });
    } catch (error) {
      console.error('Error updating prompt:', error);
      alert('Error updating prompt. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePrompt = async () => {
    if (!user || !prompt) return;
    const promptId = prompt.id || prompt.promptId;
    if (!promptId) return;

    const confirmed = window.confirm(
      'Are you sure you want to delete this prompt? This action cannot be undone.'
    );
    if (!confirmed) return;

    try {
      const deleted = await deletePromptDoc(promptId);
      if (!deleted) {
        // eslint-disable-next-line no-alert
        alert('Failed to delete prompt. Please try again.');
        return;
      }
      // eslint-disable-next-line no-alert
      alert('Prompt deleted successfully.');
      window.location.assign('/home');
    } catch (error) {
      console.error('Error deleting prompt:', error);
      // eslint-disable-next-line no-alert
      alert('Error deleting prompt. Please try again.');
    }
  };

  const openEmailModal = () => {
    setEmailInput(user?.lastPromptEmailSent ?? '');
    setEmailError('');
    setShowEmailModal(true);
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setEmailError('');
  };

  const handleSendEmail = async () => {
    const toEmail = emailInput.trim();
    if (!toEmail) {
      setEmailError('Email address is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(toEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailSending(true);
    setEmailError('');
    try {
      // No email backend anymore: open the user's own mail client with the
      // prompt prefilled instead of sending server-side.
      const subject = encodeURIComponent(`Prompt: ${prompt.title || 'Shared prompt'}`);
      const body = encodeURIComponent(
        `${prompt.title || ''}\n\n${prompt.description || ''}\n\n${prompt.promptText || ''}`
      );
      window.location.href = `mailto:${encodeURIComponent(toEmail.toLowerCase())}?subject=${subject}&body=${body}`;
      setShowEmailModal(false);
      setEmailError('');
    } catch (err) {
      setEmailError(err.message || 'Failed to open your email client. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  // Get categories array, fallback to single category for backward compatibility
  let displayCategories = [];
  if (prompt.categories && Array.isArray(prompt.categories) && prompt.categories.length > 0) {
    displayCategories = prompt.categories;
  } else if (prompt.category) {
    displayCategories = [prompt.category];
  }

  const emailModal = showEmailModal && createPortal(
    <div className="email-prompt-overlay" onClick={closeEmailModal} role="dialog" aria-modal="true" aria-labelledby="email-prompt-title">
      <div className="add-context-variable-form-container email-prompt-modal-inner" onClick={(e) => e.stopPropagation()}>
        <h2 id="email-prompt-title" className="add-form-title">Email prompt</h2>
        <div className="form-input-group">
          <label className="form-label headline-medium" htmlFor="email-prompt-input">Email</label>
          <input
            id="email-prompt-input"
            type="email"
            className={`form-input ${emailError ? 'error' : ''}`}
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.target.value);
              setEmailError('');
            }}
            placeholder="Enter email address"
            autoComplete="email"
            disabled={emailSending}
          />
          {emailError && <div className="form-error">{emailError}</div>}
        </div>
        <div className="form-actions">
          <Button type="button" variant="primary" onClick={handleSendEmail} disabled={emailSending}>
            {emailSending ? 'Sending...' : 'Send'}
          </Button>
          <Button type="button" variant="secondary" onClick={closeEmailModal} disabled={emailSending}>
            Cancel
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <>
    <div className="prompt-header">
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <linearGradient id="favorite-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#F93AAC" />
            <stop offset="100%" stopColor="#FF613E" />
          </linearGradient>
        </defs>
      </svg>
      <div className="prompt-header-row">
        <div className="prompt-header-left">
          {!isEditMode && displayCategories.length > 0 && (
            <div className="prompt-category">
              {displayCategories.map((category, index) => (
                <span key={index} className="category-label">{category}</span>
              ))}
            </div>
          )}
          {isEditMode ? (
            <input
              type="text"
              className="editable-title-input headline-large"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter prompt title"
            />
          ) : (
            <h1 className="prompt-detail-title">{prompt.title}</h1>
          )}
        </div>
        <div className="prompt-header-actions">
          {canEdit && (
            <button
              className="action-button edit-button"
              onClick={isEditMode ? handleCancelEdit : handleEdit}
              aria-label="Edit prompt"
            >
              <FaPenToSquare className="action-icon" />
            </button>
          )}
          {!isEditMode && canDeletePrompt && (
            <button
              type="button"
              className="action-button"
              onClick={handleDeletePrompt}
              aria-label="Delete prompt"
            >
              <FaTrash className="action-icon" />
            </button>
          )}
          {!isEditMode && (
            <button
              className="action-button email-button"
              onClick={openEmailModal}
              aria-label="Email prompt"
            >
              <FaEnvelope className="action-icon" />
            </button>
          )}
          <PromptActionButtons 
            prompt={prompt} 
            onUpdate={onUpdate}
            onDuplicate={onDuplicate}
            hideDuplicate={activeVersion === 'duplicate'}
            isDuplicate={activeVersion === 'duplicate'}
            className=""
            buttonClassName="action-button"
          />
        </div>
      </div>
      {isEditMode && (
        <div className="add-prompt-form">
          <div className="form-grid">
            {/* Category Dropdown */}
            <div className="form-category-section">
              <CategoryDropdown
                categories={categories}
                value={formData.category}
                onChange={(value) => handleInputChange('category', value)}
                onAddNew={handleAddCategory}
              />
            </div>

            {/* Description */}
            <div className="form-input-group description-input-group">
              <label className="form-label headline-medium">Description</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter a description for this prompt..."
                rows={3}
              />
            </div>

            {/* Format Tabs */}
            <div className="form-format-section">
              <PromptFormatTabs
                activeFormat={promptFormat}
                onFormatChange={handleFormatChange}
              />
            </div>

            {/* Format-specific content */}
            {promptFormat === 'STRUCTURED' && (
              <>
                {/* Persona */}
                <PersonaInput
                  value={formData.persona}
                  onChange={(value) => handleInputChange('persona', value)}
                />

                {/* Format */}
                <FormatInput
                  value={formData.format}
                  onChange={(value) => handleInputChange('format', value)}
                />

                {/* Context */}
                <ContextInput
                  value={formData.context}
                  onChange={(value) => handleInputChange('context', value)}
                />

                {/* Task */}
                <TaskInput
                  value={formData.task}
                  onChange={(value) => handleInputChange('task', value)}
                />

                {/* Dos and Don'ts */}
                <div className="form-row">
                  <DosInput
                    value={formData.dos}
                    onChange={(value) => handleInputChange('dos', value)}
                  />
                  <DontsInput
                    value={formData.donts}
                    onChange={(value) => handleInputChange('donts', value)}
                  />
                </div>

                {/* Examples */}
                <ExamplesInput
                  value={formData.examples}
                  onChange={(value) => handleInputChange('examples', value)}
                />
              </>
            )}

            {promptFormat === 'FREE_TEXT' && (
              <div className="form-input-group">
                <label className="form-label headline-medium">Prompt Text</label>
                <textarea
                  className="form-textarea"
                  value={formData.freeText}
                  onChange={(e) => handleInputChange('freeText', e.target.value)}
                  placeholder="Enter your prompt text here..."
                  rows={12}
                />
              </div>
            )}

            {/* Background Theory */}
            <BackgroundTheoryInput
              value={formData.backgroundTheory}
              onChange={(value) => handleInputChange('backgroundTheory', value)}
            />
          </div>

          <div className="form-actions">
            {/* Organization Assignment (Admin Only) */}
            {isAdmin && (
              <div className="form-input-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label headline-medium">Organization Assignment</label>
                <select
                  value={selectedOrgId || ''}
                  onChange={(e) => setSelectedOrgId(e.target.value === '' ? null : e.target.value)}
                  className="form-input"
                  disabled={loadingOrgs || saving}
                  style={{ cursor: loadingOrgs ? 'wait' : 'pointer' }}
                >
                  <option value="">Global (all organizations)</option>
                  {orgDomains.map((org) => (
                    <option key={org.orgId} value={org.orgId}>
                      {org.domain}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                  {selectedOrgId === null 
                    ? 'Global prompts are visible to all users regardless of organization. Vote counts are organization-specific.'
                    : `This prompt will be assigned to ${orgDomains.find(o => o.orgId === selectedOrgId)?.domain || selectedOrgId}. Only users from this organization will see it.`
                  }
                  {currentOrgDomain && ` Current: ${currentOrgDomain}`}
                </p>
              </div>
            )}
            
            <Button
              type="button"
              variant="primary"
              onClick={handleSaveEdit}
              disabled={saving}
              className="add-prompt-submit-button"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancelEdit}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
    {emailModal}
    </>
  );
};

export default PromptHeader;

